import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiMessageCircle, FiCheck, FiPause, FiPlay, FiExternalLink, FiEdit2, FiArchive, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { FREQUENCY_LABELS } from '../../services/billing';

const COUNTRY_FLAGS_MAP = { SG: '🇸🇬', LK: '🇱🇰', US: '🇺🇸', GB: '🇬🇧', AU: '🇦🇺', CA: '🇨🇦', IN: '🇮🇳', MY: '🇲🇾', TH: '🇹🇭', ID: '🇮🇩', PH: '🇵🇭', JP: '🇯🇵', KR: '🇰🇷', CN: '🇨🇳', HK: '🇭🇰', TW: '🇹🇼', NZ: '🇳🇿', AE: '🇦🇪', SA: '🇸🇦', DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', ES: '🇪🇸', NL: '🇳🇱', BR: '🇧🇷', MX: '🇲🇽', ZA: '🇿🇦', NG: '🇳🇬', KE: '🇰🇪', EG: '🇪🇬', PK: '🇵🇰', BD: '🇧🇩', VN: '🇻🇳' };
const COUNTRY_NAMES_MAP = { SG: 'Singapore', LK: 'Sri Lanka', US: 'United States', GB: 'United Kingdom', AU: 'Australia', CA: 'Canada', IN: 'India', MY: 'Malaysia', TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines', JP: 'Japan', KR: 'South Korea', CN: 'China', HK: 'Hong Kong', TW: 'Taiwan', NZ: 'New Zealand', AE: 'UAE', SA: 'Saudi Arabia', DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain', NL: 'Netherlands', BR: 'Brazil', MX: 'Mexico', ZA: 'South Africa', NG: 'Nigeria', KE: 'Kenya', EG: 'Egypt', PK: 'Pakistan', BD: 'Bangladesh', VN: 'Vietnam' };

function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function formatCurrency(amount, currency) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(amount);
}

function formatDate(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(dateString) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateString + 'T00:00:00');
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

export default function ClientList({ clients, busyId, onPaid, onToggle, onEdit, onArchive, onRestore, onDelete }) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const today = localToday();

  const visible = clients.filter(client => {
    const matches =
      client.client_name.toLowerCase().includes(query.toLowerCase()) ||
      client.phone_number.includes(query) ||
      (client.website_url || '').toLowerCase().includes(query.toLowerCase());
    const due = client.is_active && !client.is_archived && client.next_due_date <= today;

    if (filter === 'Archived') return matches && client.is_archived;
    if (filter === 'Due') return matches && due && !client.is_archived;
    if (filter === 'Active') return matches && client.is_active && !due && !client.is_archived;
    if (filter === 'Paused') return matches && !client.is_active && !client.is_archived;
    return matches && !client.is_archived;
  });

  const filters = ['All', 'Due', 'Active', 'Paused', 'Archived'];
  const archivedCount = clients.filter(c => c.is_archived).length;

  return (
    <section className="mt-8">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Clients</h2>
              <p className="text-sm text-slate-500">{visible.length} of {clients.length} clients shown</p>
            </div>
            <div className="relative w-full lg:w-72">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                aria-label="Search clients"
                placeholder="Search by name, phone, or URL..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {filters.map((item) => (
              <button
                key={item}
                onClick={() => setFilter(item)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${filter === item ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                {item}
                {item === 'Due' && visible.filter(c => c.is_active && !c.is_archived && c.next_due_date <= today).length > 0 && (
                  <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] text-white">
                    {visible.filter(c => c.is_active && !c.is_archived && c.next_due_date <= today).length}
                  </span>
                )}
                {item === 'Archived' && archivedCount > 0 && (
                  <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-500 text-[10px] text-white">
                    {archivedCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Client List */}
        <div className="divide-y divide-slate-100">
          {visible.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
                <SafeIcon icon={FiSearch} className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">No clients match this view</p>
              <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter</p>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {visible.map((client, index) => {
                const due = client.is_active && !client.is_archived && client.next_due_date <= today;
                const busy = busyId === client.id;
                const days = daysUntil(client.next_due_date);
                const freqLabel = FREQUENCY_LABELS[client.billing_frequency] || 'Monthly';
                const flag = COUNTRY_FLAGS_MAP[client.country] || '🏳️';
                const countryLabel = COUNTRY_NAMES_MAP[client.country] || client.country;
                const isArchived = filter === 'Archived' || client.is_archived;

                return (
                  <motion.div
                    key={client.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className={`group flex flex-col gap-4 p-5 md:p-6 transition lg:flex-row lg:items-center lg:justify-between ${isArchived ? 'bg-slate-50/80 hover:bg-slate-100/50' : 'hover:bg-slate-50/50'}`}
                  >
                    {/* Client Info */}
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-bold text-lg ${isArchived ? 'bg-slate-200 text-slate-500' : due ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {client.client_name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-bold truncate ${isArchived ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                            {client.client_name}
                          </h3>
                          <span className="text-base" title={countryLabel}>{flag}</span>
                          {isArchived ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide bg-slate-200 text-slate-600">
                              <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                              Archived
                            </span>
                          ) : (
                            <>
                              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${!client.is_active ? 'bg-slate-100 text-slate-500' : due ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${!client.is_active ? 'bg-slate-400' : due ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                                {!client.is_active ? 'Paused' : due ? 'Due' : 'Active'}
                              </span>
                            </>
                          )}
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-indigo-600">
                            {freqLabel}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                          <span className="font-semibold text-slate-700">
                            {formatCurrency(client.monthly_amount, client.currency)}
                            <span className="font-normal text-slate-400"> /{client.billing_frequency === 'monthly' ? 'mo' : client.billing_frequency === 'quarterly' ? 'qtr' : client.billing_frequency === 'semi_annual' ? '6mo' : 'yr'}</span>
                          </span>
                          {!isArchived && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className={`text-sm ${due ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                                {due
                                  ? (days === 0 ? 'Due today' : `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`)
                                  : (days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due in ${days} days`)}
                              </span>
                              <span className="text-slate-300">•</span>
                              <span className="text-slate-500">{formatDate(client.next_due_date)}</span>
                            </>
                          )}
                        </div>
                        {client.website_url && (
                          <a
                            href={client.website_url.startsWith('http') ? client.website_url : `https://${client.website_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                            {client.website_url.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 lg:gap-3">
                      {isArchived ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onRestore(client)}
                            className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-50"
                          >
                            <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
                            <span className="hidden sm:inline">Restore</span>
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Permanently delete "${client.client_name}"? This cannot be undone.`)) {
                                onDelete(client);
                              }
                            }}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </>
                      ) : (
                        <>
                          <a
                            href={`https://wa.me/${client.phone_number.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                          >
                            <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                            <span className="hidden sm:inline">WhatsApp</span>
                          </a>
                          {client.is_active && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => onPaid(client)}
                              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                            >
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                              <span className="hidden sm:inline">Mark Paid</span>
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onEdit(client)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50"
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onToggle(client)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50"
                          >
                            <SafeIcon icon={client.is_active ? FiPause : FiPlay} className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onArchive(client)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-400 transition hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 disabled:opacity-50"
                            title="Archive client"
                          >
                            <SafeIcon icon={FiArchive} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </div>
    </section>
  );
}